import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
    IClosingNote,
    ICreateNoteRequest,
    IUpdateNoteRequest,
    NoteKindWire,
} from '../services/closing-note.service';

/**
 * Kommentare und Pendenzen — REV-21374, ADR-0019.
 *
 * ⚠️ **Jede Aktion traegt `kind`.** Das ist kein Zufall und keine Bequemlichkeit: die beiden
 * Register sind zwei Ansichten desselben Aggregats (E1), und eine Antwort, die ihre Art verloren
 * hat, laesst sich nicht mehr der richtigen Liste zuordnen. Eine spaet eintreffende Antwort wird
 * gegen die Art im State geprueft und sonst verworfen — sonst erschienen Pendenzen im
 * Kommentar-Register, genau der Fehler, den AC9 ausschliesst.
 *
 * ⚠️ Ebenso traegt jede Aktion ihre `projectId`. Wer den Abschluss mitten im Laden wechselt, soll
 * nicht die Liste des vorherigen sehen.
 */
export const closingNoteActions = createActionGroup({
    source: 'ABCP Closing Notes',
    events: {
        /**
         * Die Liste EINER Art. `accountId` gesetzt ⇒ der Konto-Bereich fragt; `documentId` gesetzt ⇒
         * der Beleg-Viewer fragt (REV-21369). Höchstens eines von beiden.
         */
        'Load Notes': props<{ projectId: string; kind: NoteKindWire; accountId?: string; documentId?: string }>(),
        'Load Notes Success': props<{
            projectId: string;
            kind: NoteKindWire;
            accountId?: string;
            documentId?: string;
            notes: IClosingNote[];
        }>(),
        'Load Notes Failure': props<{
            projectId: string;
            kind: NoteKindWire;
            accountId?: string;
            documentId?: string;
            error: string;
        }>(),

        'Load Note': props<{ projectId: string; kind: NoteKindWire; noteId: string }>(),
        'Load Note Success': props<{ projectId: string; note: IClosingNote }>(),
        /**
         * ⚠️ `notFound` unterscheidet „gibt es nicht" von „liess sich nicht laden" (AC9). Ein
         * geteilter Link auf einen geloeschten Eintrag ist kein Systemfehler, und beides in einem
         * Fehlerzustand zusammenzufassen schickte den Leser auf die Suche nach einer Stoerung.
         */
        'Load Note Failure': props<{ projectId: string; noteId: string; error: string; notFound: boolean }>(),

        'Create Note': props<{ projectId: string; request: ICreateNoteRequest }>(),
        'Create Note Success': props<{ projectId: string; note: IClosingNote }>(),
        'Create Note Failure': props<{ projectId: string; error: string }>(),

        'Update Note': props<{ projectId: string; noteId: string; request: IUpdateNoteRequest }>(),
        'Update Note Success': props<{ projectId: string; note: IClosingNote }>(),
        'Update Note Failure': props<{ projectId: string; noteId: string; error: string }>(),

        'Delete Note': props<{ projectId: string; noteId: string }>(),
        'Delete Note Success': props<{ projectId: string; noteId: string }>(),
        'Delete Note Failure': props<{ projectId: string; noteId: string; error: string }>(),

        'Set Note Done': props<{ projectId: string; noteId: string; done: boolean }>(),
        'Set Note Done Success': props<{ projectId: string; note: IClosingNote }>(),
        'Set Note Done Failure': props<{ projectId: string; noteId: string; error: string }>(),

        'Reply To Note': props<{ projectId: string; noteId: string; text: string; mentionUserIds: string[] }>(),
        'Reply To Note Success': props<{ projectId: string; note: IClosingNote }>(),
        'Reply To Note Failure': props<{ projectId: string; noteId: string; error: string }>(),

        // ── Ansichtszustand: gehoert in den Store, weil die Pure Views kontrolliert sind ──────
        'Set Comment Filter': props<{ filter: 'all' | 'internal' | 'closingMeeting' }>(),
        'Set Todo Status': props<{ status: 'open' | 'done' }>(),
        'Set Todo Audience': props<{ audience: 'all' | 'todo' | 'reviewer' | 'client' }>(),
        'Set Sort': props<{ key: string | null; direction: 'asc' | 'desc' }>(),
        'Remove Filter': props<{ key: string }>(),
        'Reset Filters': emptyProps(),
        'Set Search Term': props<{ term: string }>(),
        'Toggle Thread': props<{ noteId: string }>(),
        'Start Edit': props<{ noteId: string | null }>(),
        'Open Composer': props<{ kind: NoteKindWire | null }>(),
        /** Die zuletzt abgehakte Zeile bleibt stehen, bis sie geraeumt wird (AC8). */
        'Clear Recently Changed': emptyProps(),
    },
});
